<template>
  <v-container class="accounts-configuration">
    <v-row justify="center">
      <ViewHeader :title="viewTitle" />

      <v-col cols="12">
        <template>
          <v-row v-if="facilitiesCount" no-gutters class="accounts">
            <v-col cols="12">
              <v-card class="main py-8 px-8">
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-data-table
                      :headers="headers"
                      :items="integrations"
                      item-key="catalog"
                      hide-default-footer
                    >
                      <template #item.status="{ item:{status, is_enabled} }">
                        <div class="status-cell">
                          <IntegrationStatusIndicator
                            :type="status.type"
                            :is-disabled="!is_enabled"
                            class="mr-2"
                          />
                          {{ status.name }}
                        </div>
                      </template>
                      <template
                        #item.actions="{item:{status: {progress}, is_enabled, type}}"
                      >
                        <div class="action-cell">
                          <ToggleFacebookIntegrationButton
                            v-if="
                              shouldShowFacebookIntegrationButton(
                                type,
                                progress
                              )
                            "
                            :progress="progress"
                          />
                          <ToggleAppleIntegrationButton
                            v-else-if="type === MAP_INTEGRATIONS_ENUM.APPLE"
                            :is-enabled="is_enabled"
                            :has-no-accounts="hasNoAppleAccounts"
                            @get-integrations="getMapIntegrations"
                          />
                          <ToggleButton
                            v-else
                            :is-enabled="is_enabled"
                            :integration-type="type"
                            @get-integrations="getMapIntegrations"
                          />
                        </div>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
          <v-row v-else>
            <v-col cols="12">
              <v-card style="min-height: 650px;" class="d-flex px-12">
                <div class="mx-auto d-flex align-center">
                  <div class="no-data d-flex flex-column align-center">
                    <h2 class="subtitle mb-6">
                      {{ $t("message.mapIntegrations.noFacilities.title") }}
                    </h2>
                    <p class="no-data__description text-center">
                      {{ $t("message.mapIntegrations.noFacilities.desc") }}
                    </p>
                    <v-btn color="secondary" @click="goToGMBAccounts">
                      {{ $t("message.mapIntegrations.noFacilities.button") }}
                    </v-btn>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </template>
        <SectionLoader :status="loading" :opacity="1" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import IntegrationStatusIndicator from "@/components/Permissions/IntegrationStatusIndicator.vue";
import ViewHeader from "@/components/ui/ViewHeader.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import mapIntegrationsService from "@/services/mapIntegrations.js";
import serviceHandler from "@/helpers/handlers/service";
import ToggleButton from "@/components/BusinessAddresses/ToggleButton.vue";
import ToggleFacebookIntegrationButton from "@/components/BusinessAddresses/ToggleFacebookIntegrationButton.vue";
import ToggleAppleIntegrationButton from "@/components/BusinessAddresses/ToggleAppleIntegrationButton.vue";
import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";
import { planRestrictionsCheck } from "@/helpers/plan-restrictions-check";
import { mapIntegrationsMixin } from "@/helpers/mixins/map-integrations";
import appleMapsService from "@/services/apple-maps";

export default {
  name: "MapSettings",
  components: {
    ViewHeader,
    SectionLoader,
    IntegrationStatusIndicator,
    ToggleButton,
    ToggleFacebookIntegrationButton,
    ToggleAppleIntegrationButton
  },
  mixins: [mapIntegrationsMixin],
  data() {
    return {
      viewTitle: this.$t("message.mapIntegrations.mapConfigurationTitle"),
      loading: false,
      headers: [
        {
          text: this.$t("message.mapIntegrations.catalog"),
          value: "catalog",
          sortable: false
        },
        {
          text: this.$t("message.mapIntegrations.integrationStatus"),
          value: "status",
          sortable: false
        },
        {
          text: this.$t("message.mapIntegrations.additionalInfo"),
          value: "additional_info",
          sortable: false
        },
        {
          text: this.$t("message.mapIntegrations.actions"),
          value: "actions",
          sortable: false,
          width: 136
        }
      ],
      integrations: [],
      facilitiesCount: 0,
      appleAccounts: []
    };
  },
  computed: {
    canManageLocationData() {
      return planRestrictionsCheck(PLAN_RESTRICTIONS.CAN_MANAGE_LOCATION_DATA);
    },
    isFacebookConfigured() {
      return false;
    },
    hasNoAppleAccounts() {
      return this.appleAccounts.length === 0;
    }
  },
  async created() {
    this.facilitiesCount = this.$store.state.company.company.facilities_count;
    await this.getAppleAccounts();
    await this.getMapIntegrations();
  },
  methods: {
    shouldShowFacebookIntegrationButton(type, progress) {
      return type === this.MAP_INTEGRATIONS_ENUM.FACEBOOK && progress !== 0;
    },
    shouldShowAppleIntegrationButton(type) {
      return type === this.MAP_INTEGRATIONS_ENUM.APPLE;
    },
    goToGMBAccounts() {
      this.$router.push({ name: "profile-gmb-accounts" });
    },
    async getAppleAccounts() {
      const accounts = await serviceHandler(appleMapsService.getAccounts());
      this.appleAccounts = accounts;
    },
    async getMapIntegrations() {
      this.loading = true;
      const apiData = await serviceHandler(
        mapIntegrationsService.getMapIntegrations()
      );
      this.setIntegrationsData(apiData);
      this.loading = false;
    },

    setIntegrationsData(apiData) {
      this.integrations = this.getIntegrationsList(apiData);
    }
  }
};
</script>

<style lang="scss" scoped>
.no-data {
  text-align: center;
  padding-top: 40px;
  max-width: 500px;
  margin: auto;
}
.action-cell {
  display: flex;
  gap: $spacing * 2;
  padding: 0 $spacing;
}

.status-cell {
  display: flex;
  align-items: center;
  padding: 0 $spacing;
}

::v-deep .v-data-table {
  .v-data-table-header {
    th {
      font-weight: normal;
      border-color: $gray-200 !important;
    }
  }
  .v-data-table__wrapper {
    tbody {
      td {
        padding: $spacing * 2 $spacing * 2;
        border-color: $gray-200 !important;
      }
    }
  }
}
</style>
